/* --- page scaffolding --------------------------------------------------- */

.pageHeader {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding-bottom: var(--space-2);
}

.pageHeaderAbove {
  min-height: 18px;
}

.pageHeaderRow {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-5);
  flex-wrap: wrap;
}

.pageHeaderText {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  min-width: 0;
}

.pageTitle {
  font-size: var(--text-xl);
  line-height: var(--text-xl-line);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  letter-spacing: -0.02em;
}

.pageDescription {
  max-width: 72ch;
  font-size: var(--text-md);
  line-height: var(--text-md-line);
  color: var(--color-text-secondary);
}

.pageHeaderActions {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  flex: none;
}

.sectionHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-5);
  min-height: 24px;
}

.sectionHeaderText {
  display: flex;
  align-items: baseline;
  gap: var(--space-4);
  min-width: 0;
}

.sectionTitle {
  font-size: var(--text-md);
  line-height: var(--text-md-line);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  white-space: nowrap;
}

.sectionDescription {
  font-size: var(--text-sm);
  line-height: var(--text-sm-line);
  color: var(--color-text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sectionHeaderActions {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  flex: none;
}

.divider {
  flex: none;
  background: var(--color-border-default);
}

.horizontal {
  width: 100%;
  height: var(--border-width-thin);
}

.vertical {
  width: var(--border-width-thin);
  align-self: stretch;
  min-height: var(--space-5);
}

.toolbar {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  min-height: var(--control-height);
}

.spacer {
  flex: 1 1 auto;
}

/* --- the register -------------------------------------------------------- *
 *
 * A panel: a white surface with a hairline edge and a low shadow, lifted off
 * the grey canvas (DL-153; it was a bare list ruled onto the page, which read
 * as one undifferentiated sheet). The heading sits in the panel's top band,
 * rows run edge to edge under it, and anything that is not a row — a form, a
 * paragraph, an alert — is inset to the same margin as the rows' text.
 */

.register {
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: var(--color-surface);
  border: var(--border-width-thin) solid var(--color-border-default);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-low);
  overflow: hidden;
  transition: box-shadow var(--duration-fast) var(--easing-standard);
}

.register:hover {
  box-shadow: var(--shadow-medium);
}

.registerHeader {
  padding: var(--space-4) var(--cell-padding-x);
  border-bottom: var(--border-width-thin) solid var(--color-border-default);
}

.registerBody {
  display: flex;
  flex-direction: column;
}

/* The rule belongs to the row above, not between the rows, so that the last
 * row closes against the panel's own edge. */
.registerBody > * {
  border-bottom: var(--border-width-thin) solid var(--color-border-default);
}

.registerBody > :last-child {
  border-bottom: 0;
}

.registerBody > :not(.row) {
  padding: var(--space-4) var(--cell-padding-x);
}

.registerFooter {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  min-height: var(--row-height);
  padding: 0 var(--cell-padding-x);
  background: var(--color-surface-sunken);
  border-top: var(--border-width-thin) solid var(--color-border-default);
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
}

/* --- rows ----------------------------------------------------------------- */

.row {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  width: 100%;
  min-height: var(--row-height);
  padding: var(--cell-padding-y) var(--cell-padding-x);
  text-align: start;
  text-decoration: none;
  color: inherit;
  background: none;
  border-radius: 0;
}

.rowInteractive {
  cursor: pointer;
  transition: background-color var(--duration-fast) var(--easing-standard);
}

.rowInteractive:hover {
  background: var(--color-surface-hover);
}

/* The ring is inset, because a row is full-bleed inside its container and an
 * offset ring would be clipped on the left and right edges. */
.rowInteractive:focus-visible {
  outline-offset: calc(var(--focus-ring-width) * -1);
  border-radius: var(--radius-sm);
}

.rowLeading {
  display: inline-flex;
  flex: none;
  align-items: center;
  color: var(--color-text-tertiary);
}

.rowMain {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  flex: 1 1 auto;
  min-width: 0;
}

.rowTrailing {
  display: flex;
  flex: none;
  align-items: center;
  gap: var(--space-4);
}

.rowTitle {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  font-size: var(--text-base);
  font-weight: var(--font-weight-medium);
  line-height: var(--text-base-line);
  color: var(--color-text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rowMeta {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  font-size: var(--text-sm);
  line-height: var(--text-sm-line);
  color: var(--color-text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* --- metrics --------------------------------------------------------------- */

.metricGroup {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: var(--space-5);
}

/* Each reading is its own tile, with a rule of the accent along its top edge
 * so that the row of tiles reads as the page's summary. */
.metric {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-5);
  background: var(--color-surface);
  border: var(--border-width-thin) solid var(--color-border-default);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-low);
  transition:
    transform var(--duration-fast) var(--easing-standard),
    box-shadow var(--duration-fast) var(--easing-standard);
}

.metric:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-medium);
}

.metricLabel {
  font-size: var(--text-xs);
  line-height: var(--text-xs-line);
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--color-text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.metricValue {
  font-size: var(--text-2xl);
  line-height: var(--text-2xl-line);
  font-weight: var(--font-weight-semibold);
  font-variant-numeric: tabular-nums lining-nums;
  letter-spacing: -0.01em;
  color: var(--color-text-primary);
}

.metricContext {
  font-size: var(--text-sm);
  line-height: var(--text-sm-line);
  color: var(--color-text-tertiary);
}
